import React, {useRef} from 'react';
import {Outlet, Link, useLocation} from 'react-router-dom';
import {motion} from 'framer-motion';
import {FaThreads, FaInstagram, FaFacebook} from 'react-icons/fa6';
import * as FiIcons from 'react-icons/fi';
import Header from './Header';
import ContentDate from './ContentDate';
import {useLanguage} from '../contexts/LanguageContext';
import SafeIcon from '../common/SafeIcon';

const {FiHeart, FiMusic, FiHome, FiInfo, FiBookOpen, FiUsers} = FiIcons;

const Layout = () => {
  const {language} = useLanguage();
  const location = useLocation();
  const mainRef = useRef(null);
  const isChinese = language === 'zh-TW';

  const footerText = {
    craftedWith: isChinese ? '匠心打造' : 'Crafted with',
    by: isChinese ? '來自' : 'by',
    designedFor: isChinese ? '為音樂愛好者傾情呈獻' : 'Designed for musicians with passion',
    home: isChinese ? '首頁' : 'Home',
    features: isChinese ? '功能介紹' : 'Features',
    howToUse: isChinese ? '使用指南' : 'How to Use',
    about: isChinese ? '關於我們' : 'About',
    copyright: isChinese ? '版權所有' : 'All rights reserved',
    followUs: isChinese ? '追蹤我們' : 'Follow Us',
  };

  const navigationLinks = [
    {path: '/', text: footerText.home, icon: FiHome},
    {path: '/features', text: footerText.features, icon: FiInfo},
    {path: '/how-to-use', text: footerText.howToUse, icon: FiBookOpen},
    {path: '/about', text: footerText.about, icon: FiUsers},
  ];

  const socialLinks = [
    {
      href: 'https://www.threads.com/@homy.hk',
      label: 'Threads',
      Icon: FaThreads,
      color: 'hover:text-gray-900 dark:hover:text-white',
    },
    {
      href: 'https://www.instagram.com/homy.hk',
      label: 'Instagram',
      Icon: FaInstagram,
      color: 'hover:text-pink-500 dark:hover:text-pink-400',
    },
    {
      href: 'https://www.facebook.com/profile.php?id=61578745554661',
      label: 'Facebook',
      Icon: FaFacebook,
      color: 'hover:text-blue-600 dark:hover:text-blue-400',
    },
  ];

  const handleHomeClick = (event, path) => {
    if (location.pathname === '/' && path === '/') {
      event.preventDefault();
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  };

  return (
    <>
      <Header />
      <main ref={mainRef} className="container mx-auto px-4 py-8 min-h-[calc(100vh-220px)]">
        {location.pathname === '/how-to-use' && (
          <ContentDate
            published="2025-08-19"
            updated="2025-08-19"
            language={language}
            className="mb-6"
          />
        )}
        <Outlet />
      </main>

      <footer className="mt-auto py-8 text-center relative overflow-hidden bg-gray-50 dark:bg-gray-800/30">
        <div className="footer-bg absolute inset-0 opacity-5"></div>
        <div className="container mx-auto px-4">
          <motion.div
            className="relative z-10"
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.2, duration: 0.6}}
          >
            <nav className="mb-6" aria-label={isChinese ? '頁尾導覽' : 'Footer navigation'}>
              <ul className="flex flex-wrap justify-center gap-4 mb-6">
                {navigationLinks.map((link) => {
                  const isActive = location.pathname === link.path;
                  return (
                    <li key={link.path}>
                      <Link
                        to={link.path}
                        onClick={(event) => handleHomeClick(event, link.path)}
                        className={`flex items-center gap-2 transition-colors px-3 py-2 rounded-md ${
                          isActive
                            ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        <SafeIcon icon={link.icon} className="w-4 h-4" />
                        <span>{link.text}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="flex flex-col items-center mb-6">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 font-medium uppercase tracking-wider">
                {footerText.followUs}
              </p>
              <div className="flex items-center gap-4">
                {socialLinks.map(({href, label, Icon, color}) => (
                  <motion.a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className={`p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors ${color}`}
                    whileHover={{scale: 1.15}}
                    whileTap={{scale: 0.95}}
                  >
                    <SafeIcon icon={Icon} className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </div>

            <div className="flex flex-col items-center justify-center">
              <motion.div
                className="heart-container mb-3"
                whileHover={{scale: 1.1}}
                initial={{scale: 0}}
                animate={{scale: 1}}
                transition={{type: 'spring', stiffness: 260, damping: 20, delay: 0.4}}
              >
                <SafeIcon icon={FiHeart} className="text-pink-500 dark:text-pink-400 heart-icon w-6 h-6 animate-pulse" />
              </motion.div>

              <motion.div
                className="max-w-md mx-auto text-center"
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                transition={{delay: 0.6}}
              >
                <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                  © {new Date().getFullYear()} Beat Master. {footerText.copyright}
                </p>
                <div className="flex flex-wrap items-center justify-center mt-2 gap-x-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {footerText.craftedWith}
                  </span>
                  <SafeIcon icon={FiHeart} className="text-pink-500 dark:text-pink-400 w-3 h-3 mx-0.5 animate-pulse" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {footerText.by}
                  </span>
                  <motion.a
                    href="https://homy.hk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="company-link inline-block"
                    whileHover={{scale: 1.03}}
                  >
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent font-bold">
                      Homy Tech Limited
                    </span>
                  </motion.a>
                </div>
                <div className="mt-3 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 space-x-1">
                  <SafeIcon icon={FiMusic} className="w-3 h-3" />
                  <span className="italic">{footerText.designedFor}</span>
                  <SafeIcon icon={FiMusic} className="w-3 h-3" />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </footer>
    </>
  );
};

export default Layout;